import Configuration from './config/Configuration.js';

// eslint
/* global cast */

export default class StreamFincons {
    /**
     * Create Stream Fincons
     * @param {Object} options
     * @param {String} options.authToken
     * @param {String} options.mediaId
     * @param {cast.framework.messages.StreamType} options.streamType
     * @param {Logger} logger
     */
    constructor(options, logger) {
        const defaultOptions = {
            authToken: null,
            mediaId: null,
            streamType: cast.framework.messages.StreamType.NONE,
            formats: 'mpeg-dash', // ISM, mpeg-dash, M3U
            drmType: 'widevine',
            subtitleFilter: false
        };
        this.options = Object.assign(defaultOptions, options);
        this.logger = logger;
    }

    call() {
        return new Promise((resolve) => {
            this.logger.info('[StreamFincons] Start to prepare endpoint');
            const { streamType, authToken } = this.options;

            let endpoint = Configuration.get('endpoint').replace('[auth]', authToken);
            endpoint = endpoint.replace('[account]', Configuration.get('accountId'));
            endpoint = endpoint.replace('[media]', this.options.mediaId);
            endpoint = endpoint.replace('[formats]', this.options.formats);

            const ads = Configuration.get('ads') || {};
            const googleAds = ads.google;
            if (ads.enabled && googleAds) {
                const isStreamLive = cast.framework.messages.StreamType.LIVE === streamType;
                const adPolicy = Configuration.get('ads.google.ad_policy') || '';
                const playerTypeKey = isStreamLive ? 'liveChannels' : 'VOD';
                const adUnitKey = authToken ? 'logged' : 'not_logged';

                const adUnit = Configuration.get(`ads.google.ad_unit.${adUnitKey}`) || '';
                const playerType = Configuration.get(`ads.google.ad_unit.type.${playerTypeKey}`) || '';
                const platform = Configuration.get('ads.google.ad_unit.platform.chromecast') || '';

                let iu = adUnit.replace('{type}', playerType);
                iu = iu.replace('{platform}', platform);

                endpoint += `&policy=${adPolicy}&iu=${iu}`;
            }
            // PRJ0930RTE-1292
            if (streamType !== cast.framework.messages.StreamType.LIVE) {
                endpoint += '&assetTypes=default:isl';
            }

            this.logger.info('[StreamFincons] Finished preparing endpoint');
            this.logger.info(`[StreamFincons] Start to fetch SMIL file, url: ${endpoint}`);
            const oReq = new XMLHttpRequest();
            oReq.addEventListener('load', (event) => {
                this.logger.info(`[StreamFincons] Finished fetching endpoint, url: ${endpoint}`);
                resolve(this.parseResponse(event));
            });
            oReq.open('GET', endpoint);
            oReq.send();
        });
    }

    parseResponse(event) {
        if (event.target.status !== 200) {
            this.logger.error(`[StreamFincons] Failed to load SMIL file, status: ${event.target.status}`);
            return {};
        }
        let xmlDoc = null;
        if (event.target.responseXML) {
            xmlDoc = event.target.responseXML;
        } else {
            const parser = new DOMParser();
            xmlDoc = parser.parseFromString(event.target.responseText, 'text/xml');
        }
        return this.parseSmilXml(xmlDoc);
    }

    parseSmilXml(xmlDoc) {
        this.logger.info('[StreamFincons] Start parsing XML smil');
        if (!xmlDoc) {
            this.logger.info('[StreamFincons] Failed to parse SMIL, missing response XML object');
            return {};
        }

        const seqEl = xmlDoc.querySelector('body seq');
        if (!seqEl) {
            this.logger.info('[StreamFincons] Failed SMIL parsing, missing <seq> element');
            return {};
        }
        const { getFirstAdEl, getFirstVideoSwitchEl, convertDataToObject } = StreamFincons;

        const adEl = getFirstAdEl(seqEl);
        const switchEl = getFirstVideoSwitchEl(seqEl);
        if (!switchEl) {
            this.logger.info('[StreamFincons] Failed SMIL parsing, missing <switch> element');
            return {};
        }
        const videoEl = switchEl.getElementsByTagName('video')[0];
        const trackingDataParamEl = switchEl.querySelector('param[name="trackingData"]');

        const adTagUrl = adEl ? adEl.getAttribute('src') || '' : '';
        const videoUrl = videoEl ? videoEl.getAttribute('src') || '' : '';
        const videoTrackingDataStr = trackingDataParamEl ? trackingDataParamEl.getAttribute('value') || '' : '';
        const videoTrackingDataObj = convertDataToObject(videoTrackingDataStr);

        const { pid: releasePid, aid: accountId } = videoTrackingDataObj;
        const hasDrm = releasePid ? true : false;
        const accountUrl = encodeURIComponent(`http://access.auth.theplatform.com/data/Account/${accountId}`);

        const streamInfo = {
            stream: this.buildVideoUrl(videoUrl),
            licenseUrl: hasDrm ? this.buildLicenseUrl({ releasePid, account: accountUrl }) : '',
            releasePid,
            adTagUrl: StreamFincons.buildAdUrl(adTagUrl)
        };

        this.logger.debug(streamInfo);
        return streamInfo;
    }

    buildVideoUrl(videoUrl) {
        const { streamType } = this.options;
        const isStreamBuffered = streamType === cast.framework.messages.StreamType.BUFFERED;
        let url = videoUrl;
        // if the stream type is LIVE, then we need add another information to the endpoint
        // TMGRIS-40
        url = this.dvrWindow(url);

        // https://ott-jira.finconsgroup.com/browse/RTE-686
        // adding url postfix for getting only one subtitle track
        if (Configuration.get('subtitleFilter') && isStreamBuffered) {
            const subtitleFilter = '(type=="video"||type=="audio"||fourcc=="stpp")';
            if (url.search(/([?&]filter=)([^&\n]*)/) > -1) {
                url = url.replace(/([?&]filter=)([^&\n]*)/, (match, p1, p2) => {
                    if (p2) { return `${p1}${p2}%26%26${subtitleFilter}`; }
                    return `${p1}${subtitleFilter}`;
                });
            } else {
                url += url.includes('?') ? '&' : '?';
                url += `filter=${subtitleFilter}`;
            }
        }
        return url;
    }

    buildLicenseUrl(params) {
        const { account, releasePid } = params;
        const { authToken } = this.options;
        const drmType = Configuration.get('drmType');

        let licenseUrl = '';
        switch (drmType) {
            case 'playready':
                licenseUrl = `https://green.playready.entitlement.theplatform.eu/playready/rightsmanager.asmx?account=${account}&auth=${authToken}&releasePid=${releasePid}&schema=1.0`;
                break;
            case 'widevine':
                licenseUrl = `https://widevine.entitlement.theplatform.eu/wv/web/ModularDrm?form=json&schema=1.0&token=${authToken}&account=${account}`;
                // let raWlicenseUrl = `https://widevine.entitlement.theplatform.eu/wv/web/ModularDrm/getRawWidevineLicense?schema=1.0&token=${authToken}&releasePid=${releasePid}&account=${account}`;
                break;
            default:
                break;
        }
        return licenseUrl;
    }

    static buildAdUrl(adUrl) {
        let url = adUrl;
        if (!url) {
            return '';
        }

        // set the `url` param in URL
        const pageUrl = window.location.href;
        if (url.search(/([?&]url=)([^&\n]*)/) > -1) {
            url = url.replace(/([?&]url=)([^&\n]*)/, `$1${pageUrl}`);
        } else {
            url += url.includes('?') ? '&' : '?';
            url += `url=${pageUrl}`;
        }

        // set the `output` param in URL
        const outputFormat = 'vmap';
        if (url.search(/([?&]output=)([^&\n]*)/) > -1) {
            url = url.replace(/([?&]output=)([^&\n]*)/, `$1${outputFormat}`);
        } else {
            url += url.includes('?') ? '&' : '?';
            url += `output=${outputFormat}`;
        }
        return url;
    }

    /**
     * To convert data in string format to object.
     * @param {String} dataString to be converted to object. E.g.: "key=value|aid=125jk5"
     * @param {String='|'} separator
     * @return {Object} output object. E.g. {key: "value", aid: "125jk5"}
     */
    static convertDataToObject(dataString, separator = '|') {
        const array = dataString.split(separator);
        const object = {};
        array.forEach((item) => {
            const arr = item.split('=');
            if (arr.length > 1) {
                const key = arr[0];
                const value = arr[1];
                object[key] = value;
            }
        });
        return object;
    }

    static getFirstAdEl(seqEl) {
        const el = seqEl.children[0];
        if (el.nodeName === 'ref' && el.getAttribute('src')) {
            return el;
        }
        return null;
    }

    static getFirstVideoSwitchEl(seqEl) {
        const switchEl = seqEl.getElementsByTagName('switch')[0];
        if (switchEl) {
            return switchEl;
        }
        return null;
    }

    dvrWindow(url) {
        let urlToChange = url;
        if (this.options.streamType === cast.framework.messages.StreamType.LIVE) {
            const dvrWindowLengthParameter = `dvr_window_length=${Configuration.get('dvrWindowLength')}`;
            const regexTime = new RegExp(/\.*(t=)(.*Z)(&)/);
            if (regexTime.test(urlToChange)) {
                urlToChange = urlToChange.replace(regexTime, `${dvrWindowLengthParameter}$3`);
            } else {
                urlToChange += (urlToChange.indexOf('?') > -1) ? '&' : '?';
                urlToChange += dvrWindowLengthParameter;
            }
        }
        return urlToChange;
    }
}
